import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlbumCard } from "@/components/AlbumCard";
import TrackList from "@/components/TrackList";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export default function Browse() {
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [genres, setGenres] = useState([]);
  const [genre, setGenre] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/genres").then((r) => setGenres(r.data.genres));
    api.get("/albums?limit=60").then((r) => setAlbums(r.data.albums));
  }, []);

  useEffect(() => {
    const url = genre === "all" ? "/tracks?limit=200" : `/tracks?genre=${encodeURIComponent(genre)}&limit=200`;
    api.get(url).then((r) => setTracks(r.data.tracks));
  }, [genre]);

  const search = (arr, keys) => arr.filter((x) => !q || keys.some((k) => (x[k] || "").toLowerCase().includes(q.toLowerCase())));
  const ft = search(tracks, ["title", "artist_name", "genre"]);
  const fa = genre === "all"
    ? search(albums, ["title", "artist_name"])
    : search(albums, ["title", "artist_name"]).filter((a) => tracks.some((t) => t.album_id === a.id));

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 pb-32">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2">The catalog</div>
      <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-6">Browse everything.</h1>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input data-testid="browse-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search tracks, albums, artists..."
                 className="rounded-none bg-black border-[#2A2A2A] pl-10 font-mono h-11" />
        </div>
      </div>

      {/* Genre pills */}
      <div data-testid="genre-filter" className="flex flex-wrap gap-2 mb-10 border-b border-[#2A2A2A] pb-6">
        <GenrePill active={genre === "all"} onClick={() => setGenre("all")} label="all" />
        {genres.map((g) => (
          <GenrePill key={g} active={genre.toLowerCase() === g.toLowerCase()} onClick={() => setGenre(g)} label={g} />
        ))}
      </div>

      <div className="mb-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl md:text-3xl">Albums <span className="text-gray-500 font-mono text-sm">({fa.length})</span></h2>
          {genre !== "all" && <span className="font-mono text-xs text-[#FF4500] uppercase tracking-widest">filter · {genre}</span>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
          {fa.map((a) => <AlbumCard key={a.id} album={a} />)}
          {fa.length === 0 && <div className="col-span-full py-10 text-center font-mono text-xs text-gray-500 uppercase tracking-widest border border-dashed border-[#2A2A2A]">No albums match this filter</div>}
        </div>
      </div>

      <div>
        <h2 className="font-display text-2xl md:text-3xl mb-4">Tracks <span className="text-gray-500 font-mono text-sm">({ft.length})</span></h2>
        <TrackList tracks={ft} contextList={ft} />
      </div>
    </div>
  );
}

function GenrePill({ active, onClick, label }) {
  return (
    <button data-testid={`genre-${label}`} onClick={onClick}
            className={`px-3 h-8 font-mono text-[11px] uppercase tracking-widest border transition-colors ${
              active
                ? "bg-[#FF4500] text-black border-[#FF4500]"
                : "border-[#2A2A2A] text-gray-300 hover:border-[#FF4500] hover:text-[#FF4500] bg-black/40"
            }`}>
      {label}
    </button>
  );
}
